import { useEffect, useMemo, useState } from 'react'
import { useAuth } from '../context/AuthContext'
import { usersAPI } from '../services/api'

interface User {
  id: string
  name: string
  email: string
  role: string
}

const AVAILABLE_ROLES = ['admin', 'moderator', 'member']

const initialFormData = {
  name: '',
  email: '',
  password: '',
  role: 'member',
}

const ROLE_COLOR: Record<string, string> = {
  admin:     'bg-[#dbeafe] text-[#1A45BF]',
  moderator: 'bg-[#fef3c7] text-[#b45309]',
  member:    'bg-[#f1f5f9] text-[#475569]',
}

const inputCls = 'w-full rounded border border-[#cbd5e1] bg-white px-3 py-2 text-sm text-[#111] outline-none focus:border-[#1E4FD8] focus:ring-2 focus:ring-[#dbeafe] transition'
const labelCls = 'block text-[11px] font-semibold text-[#555] mb-1'

function Avatar({ name }: { name: string }) {
  const initials = name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase()
  const colors = ['#1E4FD8','#7c3aed','#0891b2','#16a34a','#db2777','#d97706']
  const color = colors[name.charCodeAt(0) % colors.length]
  return (
    <div className="w-9 h-9 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0" style={{ background: color }}>
      {initials}
    </div>
  )
}

export default function UserManagementPage() {
  const { tenant } = useAuth()
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(false)
  const [showForm, setShowForm] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null)
  const [formData, setFormData] = useState(initialFormData)
  const [showPass, setShowPass] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [search, setSearch] = useState('')
  const [filterRole, setFilterRole] = useState('semua')

  useEffect(() => {
    if (tenant?.id) fetchUsers()
  }, [tenant?.id])

  useEffect(() => {
    if (success) {
      const t = setTimeout(() => setSuccess(''), 3000)
      return () => clearTimeout(t)
    }
  }, [success])

  const roleCounts = useMemo(() =>
    AVAILABLE_ROLES.reduce<Record<string, number>>((acc, role) => {
      acc[role] = users.filter(u => u.role === role).length
      return acc
    }, {}), [users])

  const filtered = useMemo(() => {
    return users.filter(u => {
      const q = search.toLowerCase()
      const matchQ = !q || u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q)
      const matchRole = filterRole === 'semua' || u.role === filterRole
      return matchQ && matchRole
    })
  }, [users, search, filterRole])

  const fetchUsers = async () => {
    if (!tenant?.id) return
    setLoading(true)
    try {
      const res = await usersAPI.list(tenant.id)
      setUsers(res.data.data || [])
    } catch {
      setError('Gagal memuat users')
    } finally {
      setLoading(false)
    }
  }

  const openAdd = () => {
    setEditingId(null)
    setFormData(initialFormData)
    setError('')
    setShowForm(true)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!tenant?.id) return
    if (!formData.name || !formData.email || (!editingId && !formData.password)) {
      setError('Nama, email, dan password wajib diisi')
      return
    }
    setLoading(true)
    setError('')
    try {
      if (editingId) {
        // edit not in original API — show success for now
        await usersAPI.update(editingId, {
          name: formData.name,
          role: formData.role,
          tenantId: tenant.id,
          ...(formData.password ? { password: formData.password } : {}),
        })
        setSuccess(`User ${formData.name} diperbarui`)
      } else {
        await usersAPI.create({ name: formData.name, email: formData.email, password: formData.password, role: formData.role, tenantId: tenant.id })
        setSuccess(`User ${formData.name} berhasil dibuat`)
      }
      setFormData(initialFormData)
      setShowForm(false)
      await fetchUsers()
    } catch (err: any) {
      setError(err.response?.data?.message || 'Gagal menyimpan user')
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (userId: string) => {
    if (!tenant?.id) return
    setLoading(true)
    setError('')
    try {
      await usersAPI.delete(userId, tenant.id)
      setSuccess('User berhasil dihapus')
      setDeleteConfirm(null)
      await fetchUsers()
    } catch (err: any) {
      setError(err.response?.data?.message || 'Gagal menghapus user')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="p-6 space-y-5">

      {/* Stat Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white rounded-xl border border-[#e2e8f0] p-4">
          <p className="text-[11px] text-[#888] font-medium">Total User</p>
          <p className="text-2xl font-bold text-[#111] mt-1">{users.length}</p>
          <p className="text-[11px] text-[#aaa] mt-0.5">aktif di workspace</p>
        </div>
        <div className="bg-white rounded-xl border border-[#e2e8f0] p-4">
          <p className="text-[11px] text-[#888] font-medium">Admin</p>
          <p className="text-2xl font-bold text-[#1E4FD8] mt-1">{roleCounts.admin || 0}</p>
          <p className="text-[11px] text-[#aaa] mt-0.5">akses penuh</p>
        </div>
        <div className="bg-white rounded-xl border border-[#e2e8f0] p-4">
          <p className="text-[11px] text-[#888] font-medium">Moderator</p>
          <p className="text-2xl font-bold text-[#b45309] mt-1">{roleCounts.moderator || 0}</p>
          <p className="text-[11px] text-[#aaa] mt-0.5">akses terbatas</p>
        </div>
        <div className="bg-white rounded-xl border border-[#e2e8f0] p-4">
          <p className="text-[11px] text-[#888] font-medium">Member</p>
          <p className="text-2xl font-bold text-[#475569] mt-1">{roleCounts.member || 0}</p>
          <p className="text-[11px] text-[#aaa] mt-0.5">akses dasar</p>
        </div>
      </div>

      {/* Alert banners */}
      {success && (
        <div className="flex items-center gap-2 bg-[#dcfce7] border border-[#86efac] text-[#15803d] text-sm font-medium px-4 py-3 rounded-xl">
          <span>✓</span> {success}
        </div>
      )}
      {error && (
        <div className="flex items-center gap-2 bg-[#fee2e2] border border-[#fca5a5] text-[#b91c1c] text-sm font-medium px-4 py-3 rounded-xl">
          <span>!</span> {error}
        </div>
      )}

      {/* Toolbar */}
      <div className="flex flex-wrap gap-2 items-center justify-between">
        <div className="flex gap-2">
          <input
            className="rounded border border-[#cbd5e1] px-3 py-1.5 text-sm outline-none focus:border-[#1E4FD8] focus:ring-2 focus:ring-[#dbeafe] w-52"
            placeholder="Cari nama atau email..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <select
            className="rounded border border-[#cbd5e1] px-3 py-1.5 text-sm outline-none focus:border-[#1E4FD8]"
            value={filterRole}
            onChange={e => setFilterRole(e.target.value)}
          >
            <option value="semua">Semua Role</option>
            {AVAILABLE_ROLES.map(r => (
              <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>
            ))}
          </select>
        </div>
        <button
          onClick={openAdd}
          className="flex items-center gap-1.5 bg-[#1E4FD8] hover:bg-[#1A45BF] text-white text-sm font-semibold px-4 py-2 rounded-lg transition"
        >
          <span className="text-base leading-none">+</span> Tambah User
        </button>
      </div>

      {/* User Table */}
      <div className="bg-white rounded-xl border border-[#e2e8f0] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#f8fafc] text-[#555] text-[11px] font-semibold uppercase tracking-wide border-b border-[#e2e8f0]">
                <th className="px-4 py-3 text-left">User</th>
                <th className="px-4 py-3 text-left">Email</th>
                <th className="px-4 py-3 text-center">Role</th>
                <th className="px-4 py-3 text-center">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {loading && !showForm && (
                <tr><td colSpan={4} className="text-center py-10 text-[#aaa]">Memuat data...</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={4} className="text-center py-10 text-[#aaa]">
                  {users.length === 0 ? 'Belum ada user. Tambahkan user baru untuk memulai.' : 'Tidak ada user ditemukan.'}
                </td></tr>
              )}
              {filtered.map(user => (
                <tr key={user.id} className="border-b border-[#f1f5f9] hover:bg-[#f8fafc] transition">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <Avatar name={user.name} />
                      <p className="font-semibold text-[#111]">{user.name}</p>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-[#555]">{user.email}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`inline-block text-[11px] font-semibold px-2.5 py-0.5 rounded-full ${ROLE_COLOR[user.role] || ROLE_COLOR.member}`}>
                      {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-center gap-1">
                      <button
                        onClick={() => {
                          setEditingId(user.id)
                          setFormData({ name: user.name, email: user.email, password: '', role: user.role })
                          setError('')
                          setShowForm(true)
                        }}
                        className="text-xs px-2.5 py-1 rounded border border-[#1E4FD8] text-[#1E4FD8] hover:bg-[#EEF3FE] transition"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => setDeleteConfirm(user.id)}
                        className="text-xs px-2.5 py-1 rounded border border-[#fca5a5] text-[#dc2626] hover:bg-[#fef2f2] transition"
                      >
                        Hapus
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-[#e2e8f0]">
              <h2 className="text-base font-bold text-[#111]">
                {editingId ? 'Edit User' : 'Tambah User Baru'}
              </h2>
              <button onClick={() => setShowForm(false)} className="text-[#aaa] hover:text-[#333] text-xl leading-none">✕</button>
            </div>
            <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
              {error && (
                <div className="bg-[#fee2e2] border border-[#fca5a5] text-[#b91c1c] text-sm px-3 py-2 rounded-lg">{error}</div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className={labelCls}>Nama *</label>
                  <input type="text" className={inputCls} placeholder="Nama lengkap"
                    value={formData.name} onChange={e => setFormData(f => ({ ...f, name: e.target.value }))} />
                </div>
                <div className="col-span-2">
                  <label className={labelCls}>Email *</label>
                  <input type="email" className={inputCls} placeholder="email@bengkel.com"
                    value={formData.email} onChange={e => setFormData(f => ({ ...f, email: e.target.value }))} />
                </div>
                <div className="col-span-2">
                  <label className={labelCls}>Password {editingId ? '(kosongkan jika tidak diubah)' : '*'}</label>
                  <div className="relative">
                    <input
                      type={showPass ? 'text' : 'password'}
                      className={inputCls + ' pr-10'}
                      placeholder={editingId ? 'Biarkan kosong' : 'Min. 8 karakter'}
                      value={formData.password}
                      onChange={e => setFormData(f => ({ ...f, password: e.target.value }))}
                    />
                    <button type="button" onClick={() => setShowPass(p => !p)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#999] hover:text-[#555] text-xs">
                      {showPass ? 'Sembunyikan' : 'Tampilkan'}
                    </button>
                  </div>
                </div>
                <div className="col-span-2">
                  <label className={labelCls}>Role *</label>
                  <select className={inputCls}
                    value={formData.role} onChange={e => setFormData(f => ({ ...f, role: e.target.value }))}>
                    {AVAILABLE_ROLES.map(r => (
                      <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>
                    ))}
                  </select>
                  <p className="text-[11px] text-[#aaa] mt-1">
                    {formData.role === 'admin' ? 'Akses penuh ke semua fitur' : formData.role === 'moderator' ? 'Bisa kelola booking & layanan' : 'Akses baca saja'}
                  </p>
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <button type="button" onClick={() => setShowForm(false)}
                  className="text-sm px-4 py-2 rounded-lg bg-[#f1f5f9] text-[#555] hover:bg-[#e2e8f0] transition">
                  Batal
                </button>
                <button type="submit" disabled={loading}
                  className="text-sm px-5 py-2 rounded-lg bg-[#1E4FD8] text-white hover:bg-[#1A45BF] font-semibold transition disabled:opacity-40">
                  {loading ? 'Menyimpan...' : editingId ? 'Simpan Perubahan' : 'Tambah User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirm Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6 space-y-4">
            <h3 className="font-bold text-[#111]">Hapus User?</h3>
            <p className="text-sm text-[#666]">User akan dihapus permanen dari workspace ini.</p>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setDeleteConfirm(null)}
                className="text-sm px-4 py-2 rounded-lg bg-[#f1f5f9] text-[#555] hover:bg-[#e2e8f0] transition">
                Batal
              </button>
              <button onClick={() => handleDelete(deleteConfirm)} disabled={loading}
                className="text-sm px-4 py-2 rounded-lg bg-[#dc2626] text-white hover:bg-[#b91c1c] transition font-semibold disabled:opacity-40">
                {loading ? 'Menghapus...' : 'Hapus'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
